import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search, Filter, Package, CheckCircle2, XCircle, Star, Eye, Loader2, Plus, Trash2,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { formatNPR } from '@/utils/format';
import { adminApi } from '@/services/api';
import { unwrapList, toNumber } from '@nepal-ecom/shared-utils';
import { STOREFRONT_URL } from '@/lib/cms';
import { cn } from '@nepal-ecom/ui';
import { Badge, Button, Card, CardContent, Input, useConfirm } from '@nepal-ecom/ui';

type ApiProductStatus = 'draft' | 'active' | 'out_of_stock' | 'archived';

interface ApiProduct {
  id: string;
  name: string;
  sku?: string | null;
  slug: string;
  price: string | number;
  stock: number;
  status: ApiProductStatus;
  isFeatured: boolean;
  vendorId: string;
  categoryId: string;
  thumbnailUrl?: string | null;
  createdAt: string;
  vendor?: { businessName?: string; storeName?: string };
  category?: { name?: string };
}

const STATUS_META: Record<ApiProductStatus, { label: string; badge: 'warning' | 'success' | 'destructive' | 'default'; tone: string }> = {
  draft:        { label: 'Pending review', badge: 'warning',     tone: 'bg-amber-50 text-amber-700' },
  active:       { label: 'Approved',       badge: 'success',     tone: 'bg-emerald-50 text-emerald-700' },
  out_of_stock: { label: 'Out of stock',   badge: 'destructive', tone: 'bg-red-50 text-red-700' },
  archived:     { label: 'Archived',       badge: 'default',     tone: 'bg-slate-50 text-slate-600' },
};

export function ProductList() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  // Seed from the global top-bar search (?search=) so it actually filters here.
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  const [statusFilter, setStatusFilter] = useState<'all' | ApiProductStatus>('all');
  const [list, setList] = useState<ApiProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      // `status=all` is the admin/vendor escape hatch that disables the public ACTIVE-only filter.
      const res = await adminApi.getProducts({ status: 'all', limit: 500 });
      setList(unwrapList<ApiProduct>(res.data?.data));
    } catch (e) {
      toast.error('Could not load products', { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  /** Optimistically patch a single row, used by approve/reject/feature to avoid re-fetching everything. */
  const patchRow = (id: string, patch: Partial<ApiProduct>) =>
    setList((arr) => arr.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  useEffect(() => { void load(); }, []);

  const counts = useMemo(() => ({
    all: list.length,
    draft: list.filter((p) => p.status === 'draft').length,
    active: list.filter((p) => p.status === 'active').length,
    out_of_stock: list.filter((p) => p.status === 'out_of_stock').length,
    archived: list.filter((p) => p.status === 'archived').length,
  }), [list]);

  const filtered = useMemo(() => {
    let arr = list;
    if (statusFilter !== 'all') arr = arr.filter((p) => p.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        (p.sku ?? '').toLowerCase().includes(q) ||
        (p.vendor?.businessName ?? p.vendor?.storeName ?? '').toLowerCase().includes(q),
      );
    }
    return arr;
  }, [list, search, statusFilter]);

  const approve = async (p: ApiProduct) => {
    setActing(p.id);
    try {
      await adminApi.approveProduct(p.id);
      patchRow(p.id, { status: 'active' });
      toast.success('Product approved', { description: p.name });
    } catch (e) {
      toast.error('Approve failed', { description: (e as Error).message });
    } finally {
      setActing(null);
    }
  };

  const reject = async (p: ApiProduct) => {
    if (!(await confirm({
      title: `Archive "${p.name}"?`,
      description: 'It will be hidden from the storefront. You can re-publish it later.',
      confirmLabel: 'Archive',
      tone: 'danger',
    }))) return;
    setActing(p.id);
    try {
      await adminApi.rejectProduct(p.id);
      patchRow(p.id, { status: 'archived' });
      toast.error('Product archived', { description: p.name });
    } catch (e) {
      toast.error('Reject failed', { description: (e as Error).message });
    } finally {
      setActing(null);
    }
  };

  const remove = async (p: ApiProduct) => {
    if (!(await confirm({
      title: `Delete "${p.name}"?`,
      description: 'This permanently removes the product and its variants. Existing order history is kept. This cannot be undone.',
      confirmLabel: 'Delete permanently',
      tone: 'danger',
    }))) return;
    setActing(p.id);
    try {
      await adminApi.deleteProduct(p.id);
      setList((arr) => arr.filter((x) => x.id !== p.id));
      toast.success('Product deleted', { description: p.name });
    } catch (e) {
      toast.error('Delete failed', { description: (e as Error).message });
    } finally {
      setActing(null);
    }
  };

  const toggleFeature = async (p: ApiProduct) => {
    const next = !p.isFeatured;
    setActing(p.id);
    try {
      await adminApi.featureProduct(p.id, next);
      patchRow(p.id, { isFeatured: next });
      toast.success(next ? 'Featured' : 'Unfeatured', { description: p.name });
    } catch (e) {
      toast.error('Update failed', { description: (e as Error).message });
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Product Catalog"
        description="Platform-wide moderation, featured curation, and inventory triage."
        actions={
          <Button size="sm" onClick={() => navigate('/products/new')}>
            <Plus className="h-4 w-4" /> Add product
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[
          { key: 'all',          label: 'Total',         value: counts.all,          tone: 'bg-slate-100 text-slate-700' },
          { key: 'draft',        label: 'Pending',       value: counts.draft,        tone: STATUS_META.draft.tone },
          { key: 'active',       label: 'Approved',      value: counts.active,       tone: STATUS_META.active.tone },
          { key: 'out_of_stock', label: 'Out of stock',  value: counts.out_of_stock, tone: STATUS_META.out_of_stock.tone },
          { key: 'archived',     label: 'Archived',      value: counts.archived,     tone: STATUS_META.archived.tone },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key === 'all' ? 'all' : (s.key as ApiProductStatus))}
            className={cn(
              'rounded-lg border bg-white px-4 py-3 text-left transition-all hover:shadow-sm',
              statusFilter === s.key ? 'border-primary-300 ring-2 ring-primary-100' : 'border-slate-200',
            )}
          >
            <p className={cn('inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded', s.tone)}>{s.label}</p>
            <p className="text-2xl font-bold text-slate-900 tabular-nums mt-1.5">{loading ? '—' : s.value}</p>
          </button>
        ))}
      </div>

      <Card className="mb-5">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, SKU, or vendor…" className="pl-9" />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'all' | ApiProductStatus)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="all">All status</option>
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Package} title="No products match" description="Adjust search or filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {['Product', 'Vendor', 'Category', 'Price', 'Stock', 'Status', 'Submitted', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((p) => {
                  const meta = STATUS_META[p.status];
                  return (
                    <tr
                      key={p.id}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button')) return;
                        navigate(`/products/${p.id}`);
                      }}
                      className="hover:bg-primary-50/40 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-md bg-slate-100 overflow-hidden flex-shrink-0">
                            {p.thumbnailUrl
                              ? <img src={p.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                              : <Package className="h-4 w-4 m-3 text-slate-400" />}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5 max-w-xs">
                              {p.name}
                              {p.isFeatured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400 flex-shrink-0" />}
                            </p>
                            <p className="text-xs text-slate-500 tabular-nums truncate">{p.sku ?? `SKU-${p.id.slice(-6).toUpperCase()}`}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-slate-700 truncate max-w-xs">
                        {p.vendor?.businessName ?? p.vendor?.storeName ?? '—'}
                      </td>
                      <td className="px-4 py-3.5 text-xs text-slate-500 truncate max-w-xs">
                        {p.category?.name ?? '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm font-bold text-slate-900 tabular-nums">{formatNPR(toNumber(p.price), true)}</td>
                      <td className="px-4 py-3.5">
                        <span className={cn(
                          'text-sm tabular-nums font-semibold',
                          p.stock === 0 ? 'text-red-600' : p.stock < 10 ? 'text-amber-600' : 'text-slate-700',
                        )}>{p.stock}</span>
                      </td>
                      <td className="px-4 py-3.5"><Badge variant={meta.badge}>{meta.label}</Badge></td>
                      <td className="px-4 py-3.5 text-xs text-slate-500 whitespace-nowrap">
                        {new Date(p.createdAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1">
                          {p.status === 'draft' && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600 hover:bg-emerald-50"
                                title="Approve & publish" disabled={acting === p.id} onClick={() => approve(p)}>
                                {acting === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:bg-red-50"
                                title="Archive" disabled={acting === p.id} onClick={() => reject(p)}>
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          {(p.status === 'active' || p.status === 'out_of_stock') && (
                            <>
                              <Button variant="ghost" size="icon"
                                className={cn('h-7 w-7', p.isFeatured ? 'text-amber-600' : 'text-slate-400')}
                                title={p.isFeatured ? 'Unfeature' : 'Feature'} disabled={acting === p.id}
                                onClick={() => toggleFeature(p)}>
                                <Star className={cn('h-3.5 w-3.5', p.isFeatured && 'fill-amber-400')} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:bg-red-50"
                                title="Archive" disabled={acting === p.id} onClick={() => reject(p)}>
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          {p.status === 'archived' && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600 hover:bg-emerald-50"
                              title="Re-publish" disabled={acting === p.id} onClick={() => approve(p)}>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {STOREFRONT_URL && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Preview on storefront" asChild>
                              <a href={`${STOREFRONT_URL}/product/${p.slug}`} target="_blank" rel="noopener noreferrer">
                                <Eye className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:bg-red-50"
                            title="Delete permanently" disabled={acting === p.id} onClick={() => remove(p)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
